import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  styled,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

// import { ExistingImage } from './index';
import { serviceManagementService } from '../../services/serviceManagementService';
// const IMG_BASE_URL = 'http://protoncarwashapi.csnsindia.com:7089'; // or your actual API base URL
// const IMG_BASE_URL = 'http://localhost:7088'; // or your actual API base URL
import { IMG_PATH_URL } from '../../services/api';

interface ExistingImages {
  image_id: string | number;
  image_url: string;
}

interface ServiceImagesFormProps {
  images: File[];
  onImagesChange: (images: File[]) => void;
  existingImages?: ExistingImages[];
  onRemoveExistingImage?: (id: string | number) => void; // ✅ optional delete handler
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled(Paper)(({ theme }) => ({
  position: 'relative',
  paddingTop: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '&:hover .delete-button': {
    opacity: 1,
  },
}));

const DeleteButton = styled(IconButton)({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
});

const ServiceImagesForm: React.FC<ServiceImagesFormProps> = ({
  images,
  onImagesChange,
  existingImages,
  onRemoveExistingImage,
  
}) => {
  // Handle new uploads
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const newImages = Array.from(event.target.files);
        onImagesChange([...images, ...newImages]);
      }
    },
    [images, onImagesChange]
  );

  // Remove newly uploaded image
  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Service Images
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        Upload high-quality images that showcase your service. The first image will be used as the main image.
      </Typography>

      {/* ✅ Existing Images Section */}
      {existingImages && existingImages.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Existing Images
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
            {existingImages.map((img) => (
              <Box key={img.image_id} sx={{ position: 'relative' }}>
                <img
                  src={IMG_PATH_URL + img.image_url}
                  alt={`Service Image ${img.image_id}`}
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                  }}
                />
                {onRemoveExistingImage && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveExistingImage(img.image_id)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'error.light' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}

      {/* Upload Button */}
      <Box mb={4}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          sx={{ mb: 2 }}
        >
          Upload Images
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
          />
        </Button>
        <Typography variant="caption" display="block" color="textSecondary">
          Maximum 10 images. Supported formats: JPG, PNG, WEBP. Max size: 5MB per image.
        </Typography>
      </Box>

      {/* Newly Uploaded Images */}
      {images.length > 0 ? (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <ImagePreview elevation={3}>
                <img
                  src={URL.createObjectURL(image)}
                  alt={`Preview ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <DeleteButton
                  className="delete-button"
                  size="small"
                  onClick={() => handleRemoveImage(index)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </DeleteButton>

                {index === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      px: 1,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Main
                  </Box>
                )}
              </ImagePreview>
              <Typography
                variant="caption"
                noWrap
                display="block"
                textAlign="center"
                mt={0.5}
              >
                {image.name.length > 20
                  ? `${image.name.substring(0, 15)}...`
                  : image.name}
              </Typography>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'action.hover',
            borderRadius: 2,
          }}
        >
          <CloudUploadIcon
            sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          />
          <Typography variant="body1" color="textSecondary">
            No images uploaded yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Drag and drop images here or click the button above to upload
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ServiceImagesForm;
